import Header from '@/components/Header';
import {useUserContext} from '@/components/Providers/UserContext';
import StudentModal from '@/components/StudentModal';
import useAuthenticatedFetch from '@/hooks/useAuthenticatedFetch.ts';
import type {Directory, DirectoryStudent} from '@/types/DirectoryStudent';
import PersonIcon from '@mui/icons-material/Person';
import {Alert, Box, CircularProgress, Container, CssBaseline, Link, Stack} from '@mui/material';
import {type MRT_ColumnDef, MaterialReactTable, useMaterialReactTable} from 'material-react-table';
import type {ReactNode} from 'react';
import type * as React from 'react';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

const StudentDirectory = (): React.ReactElement => {
    const navigate = useNavigate();
    const {user} = useUserContext();
    const fetch = useAuthenticatedFetch();
    const [rentalsLoading, setRentalsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [students, setStudents] = useState<Directory>([]);
    const [selectedStudent, setSelectedStudent] = useState<DirectoryStudent | null>(null);

    const columns: MRT_ColumnDef<DirectoryStudent>[] = [
        {
            accessorKey: 'details',
            header: 'Details',
            enableSorting: false,
            enableColumnFilter: false,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Cell: (params): ReactNode => {
                return (
                    <Link
                        href='#'
                        onClick={e => {
                            e.preventDefault();
                            setSelectedStudent(params.row.original);
                        }}
                    >
                        <PersonIcon />
                    </Link>
                );
            },
        },
        {
            accessorKey: 'nameLast',
            header: 'Last Name',
        },
        {
            accessorKey: 'nameFirst',
            header: 'First Name',
        },
        {
            accessorKey: 'grade',
            header: 'Grade',
        },
    ];

    useEffect(() => {
        const doFetchRentals = async () => {
            setErrorMessage('');
            setInfoMessage('');

            const url = new URL('/v1/directory', import.meta.env.VITE_APP_API_ENDPOINT);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const json = (await response.json()) as Directory;

                setRentalsLoading(false);
                setStudents(json);

                return;
            }

            setErrorMessage('Error: Unable to fetch Active Memberships');
        };

        if (user !== undefined) {
            void doFetchRentals();
        }
    }, [user, fetch]);

    useEffect(() => {
        if (user === null) {
            navigate('/');
        }
    }, [navigate, user]);

    const table = useMaterialReactTable({
        columns,
        data: students,
        enableHiding: false,
        enableColumnActions: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        initialState: {
            showGlobalFilter: true,
            sorting: [
                {id: 'nameLast', desc: false},
                {id: 'nameFirst', desc: false},
            ],
        },
    });

    return (
        <>
            <CssBaseline />
            <Header />
            <Box
                sx={{
                    backgroundColor: '#631d79',
                    height: '100%',
                    pt: 1,
                    pb: 4,
                    minHeight: '100vh',
                }}
            >
                <Container
                    maxWidth='md'
                    sx={{
                        backgroundColor: '#fff',
                        mt: '150px',
                        pb: 2,
                    }}
                >
                    <Stack direction='row' justifyContent='center' sx={{p: 4}}>
                        <Box sx={{typography: 'h5'}}>Student Directory</Box>
                    </Stack>

                    {(rentalsLoading || user === undefined) && (
                        <Stack direction='row' justifyContent='center'>
                            <CircularProgress />
                        </Stack>
                    )}

                    {!rentalsLoading && students.length === 0 && (
                        <Stack direction='row' justifyContent='center'>
                            <Alert severity='error'>Failed to load Directory</Alert>
                        </Stack>
                    )}

                    {errorMessage !== '' && (
                        <Stack direction='row' justifyContent='center'>
                            <Alert severity='error'>{errorMessage}</Alert>
                        </Stack>
                    )}

                    {infoMessage !== '' && (
                        <Stack direction='row' justifyContent='center'>
                            <Alert severity='info'>{infoMessage}</Alert>
                        </Stack>
                    )}

                    {!rentalsLoading && students.length > 0 && <MaterialReactTable table={table} />}
                    <StudentModal
                        open={selectedStudent !== null}
                        directoryStudent={selectedStudent}
                        handleClose={() => {
                            setSelectedStudent(null);
                        }}
                    />
                </Container>
            </Box>
        </>
    );
};

export default StudentDirectory;
